import { useEffect, useRef } from 'react';
import { useAppStore } from '@/stores/app-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { apiClient } from '@/api/client';
import type { AppState } from '@/types';

/**
 * Subscribes to Zustand stores and debounces saving state to server.
 * Call this once in the root authenticated component.
 */
export function useStateSync() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Save state on changes (debounced 1s)
    const unsubApp = useAppStore.subscribe(() => scheduleSave());
    const unsubWorkspace = useWorkspaceStore.subscribe(() => scheduleSave());

    function scheduleSave() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(saveState, 1000);
    }

    function saveState() {
      const app = useAppStore.getState();
      const workspace = useWorkspaceStore.getState();

      const state: AppState = {
        root: workspace.root,
        focusedPaneId: workspace.focusedPaneId,
        sidebarCollapsed: app.sidebarCollapsed,
        bottomPanelCollapsed: app.bottomPanelCollapsed,
        sidebarWidth: app.sidebarWidth,
        bottomPanelHeight: app.bottomPanelHeight,
        theme: app.theme,
        treeExpanded: app.treeExpanded,
        tabPins: app.tabPins,
        tabGroups: app.tabGroups,
        databaseVisibility: app.databaseVisibility,
      };

      apiClient.put('/api/state', state).catch((err) => {
        console.warn('[state-sync] Failed to save:', err.message);
      });
    }

    return () => {
      unsubApp();
      unsubWorkspace();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
}

/**
 * Restore state from server and hydrate stores.
 * Call this after login.
 */
export async function restoreState(): Promise<void> {
  try {
    const state = await apiClient.get<AppState>('/api/state');
    if (!state) return;

    // Hydrate workspace — handles both new (root) and legacy (tabs) formats
    if (state.root || state.tabs || state.activeTabId) {
      useWorkspaceStore.getState().hydrate({
        root: state.root,
        focusedPaneId: state.focusedPaneId,
        tabs: state.tabs || [],
        activeTabId: state.activeTabId ?? null,
      });
    }

    const theme = state.theme ?? 'dark';
    useAppStore.getState().hydrate({
      sidebarCollapsed: state.sidebarCollapsed ?? false,
      bottomPanelCollapsed: state.bottomPanelCollapsed ?? false,
      sidebarWidth: state.sidebarWidth ?? 264,
      bottomPanelHeight: state.bottomPanelHeight ?? 180,
      theme,
      treeExpanded: state.treeExpanded ?? {},
      tabPins: state.tabPins ?? {},
      tabGroups: state.tabGroups ?? {},
      databaseVisibility: state.databaseVisibility ?? {},
    });

    // Apply theme to document immediately on restore
    document.documentElement.setAttribute('data-theme', theme);
  } catch (err) {
    console.warn('[state-sync] Failed to restore:', err);
  }
}
